import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "glass bg-primary/90 text-primary-foreground hover:bg-primary border border-primary/30 shadow-lg hover:shadow-xl",
        destructive: "glass bg-destructive/90 text-destructive-foreground hover:bg-destructive border border-destructive/30 shadow-lg",
        outline: "glass border border-primary/30 bg-background/50 hover:bg-primary/10 hover:border-primary/50",
        secondary: "glass bg-secondary/80 text-secondary-foreground hover:bg-secondary border border-border/30",
        ghost: "hover:bg-muted/50 backdrop-blur-sm hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline hover:text-primary-dark",
        glass: "glass text-foreground hover:bg-card/90 border-border/50 shadow-lg hover:shadow-xl",
        "glass-accent": "glass text-primary bg-primary/5 hover:bg-primary/10 border-primary/20 shadow-lg",
        success: "glass bg-success/90 text-success-foreground hover:bg-success border border-success/30 shadow-lg hover:shadow-xl",
        gold: "glass bg-gold/90 text-gold-foreground hover:bg-gold border border-gold/30 shadow-lg hover:shadow-xl",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3 text-xs",
        lg: "h-12 px-8 text-base",
        xl: "h-14 px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
